import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalRHub } from '@/realtime/useSignalRHub';
import { suspiciousTradesApi } from '@/api/services/suspiciousTrades';

// Global pending-alert badge count for the Suspicious Trades sidebar item
// (legacy TsAlertsBadge). Fetches the initial count, then keeps it live via the
// /hubs/suspicious-trades OnPendingAlertCountChanged push. Only runs when the
// user can see the page (enabled); returns null while unknown / disabled.
export function useSuspiciousTradesBadge(enabled: boolean): number | null {
  const [count, setCount] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const n = await queryClient.fetchQuery({
        queryKey: ['ts-pending-alert-count'],
        queryFn: suspiciousTradesApi.getPendingAlertCount,
        staleTime: 0,
      });
      setCount(Number(n));
    } catch {
      // Leave the current value on transient failures.
    }
  }, [queryClient]);

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  useSignalRHub({
    url: '/hubs/suspicious-trades',
    enabled,
    on: {
      OnPendingAlertCountChanged: (n) => setCount(Number(n)),
    },
    onConnected: (_conn, isReconnect) => {
      // Re-sync after a (re)connect so a count that changed while offline is caught.
      if (isReconnect) void refresh();
    },
  });

  return enabled ? count : null;
}
